import { EVENT_USER_LANGUAGE_CHANGED, EVENT_USER_PROFILE_CHANGED } from '@rio-cloud/rio-user-menu-component';
import type { User } from 'oidc-client-ts';
import { config } from '../config';
import { extractLanguage } from './lang/lang';
import { getLocale } from './lang/langSlice';
import { configureFetchDisplayMessages } from './lang/services';
import { type OAuthConfig, configureMockUserManager, configureUserManager, createUserManager } from './login/login';
import { userProfileObtained, userSessionExpired, userSessionRenewed } from './login/loginSlice';
import { attemptInitialSignIn } from './setup/oauth';
import { reportErrorToSentry } from './setup/sentry';
import { store } from './setup/store';
import { trace } from './setup/trace';
import { accessToken } from './tokenHandling/accessToken';
import { accessTokenStored, idTokenStored } from './tokenHandling/tokenHandlingSlice';
import type { SessionRenewedResult } from './types';

export const main = (renderApp: (user: User | null | undefined) => void) => {
    const fetchLanguageData = configureFetchDisplayMessages(store);

    // We want the `<html lang>` attribute to be synced with the
    // language currently displayed
    store.subscribe(() => {
        const lang = extractLanguage(getLocale(store.getState()));
        const html = document.querySelector('html');

        if (html && typeof lang === 'string' && lang && html.getAttribute('lang') !== lang) {
            html.setAttribute('lang', lang);
        }
    });

    const oauthConfig: OAuthConfig = {
        onSessionExpired: () => {
            accessToken.discardAccessToken();
            store.dispatch(userSessionExpired());
        },
        onSessionRenewed: (result: SessionRenewedResult) => {
            trace('index.onTokenRenewed', result);

            accessToken.saveAccessToken(result.accessToken);
            store.dispatch(accessTokenStored(result.accessToken));
            store.dispatch(idTokenStored(result.idToken));
            store.dispatch(userProfileObtained(result.profile));

            store.dispatch(userSessionRenewed());

            // You will need to get the user language by yourself then
            // you may fetch the suitable messages from the CDN. Depending
            // on when and from where you fetch the user settings you might
            // want to employ a loading spinner while the request is ongoing.
            void fetchLanguageData(result.locale);
        },
    };

    const isAllowedToMockAuth = import.meta.env.DEV;
    // enable mocking of authentication in production
    // biome-ignore lint/complexity/useOptionalChain: suppress out of convenience during migration to biome
    const capabilityMonitoringAccessToken = sessionStorage && sessionStorage.getItem('test_access_token');
    // biome-ignore lint/complexity/useOptionalChain: suppress out of convenience during migration to biome
    const testIdToken = sessionStorage && sessionStorage.getItem('test_id_token');
    const userManager =
        (isAllowedToMockAuth && config.login.mockAuthorization) || capabilityMonitoringAccessToken
            ? configureMockUserManager(oauthConfig, { capabilityMonitoringAccessToken, idToken: testIdToken, store })
            : configureUserManager(oauthConfig, createUserManager());

    document.addEventListener(EVENT_USER_LANGUAGE_CHANGED, userManager.signinSilent.bind(userManager) as EventListener);
    document.addEventListener(EVENT_USER_PROFILE_CHANGED, userManager.signinSilent.bind(userManager) as EventListener);

    attemptInitialSignIn(userManager)
        .then(renderApp)
        .catch(error => {
            trace('could not start application', error);
            reportErrorToSentry(error);
        });
};
